/** Market search: ranks instruments for a query typed by a trader ("xau", "nasdaq", "btcusdt", "apple"). */
import { instrumentTag, pairLabel } from './format.js';
/** Other names traders use for a market. Commodity codes (XAU/USD …) come from instrumentTag. */
const ALIASES = {
    BTC: ['XBT'],
    OIL: ['USOIL', 'CRUDE'],
    BRENT: ['UKOIL', 'CRUDE'],
    NATGAS: ['GAS'],
    SPX500: ['SPX', 'S&P', 'S&P500', 'SP500', 'US500'],
    NAS100: ['NASDAQ', 'NASDAQ100', 'NDX', 'US100'],
    US30: ['DOW', 'DJI', 'DJIA'],
    US2000: ['RUSSELL', 'RUT'],
    GER40: ['DAX', 'DE40'],
    UK100: ['FTSE'],
    FRA40: ['CAC'],
    EU50: ['STOXX', 'SX5E'],
    JP225: ['NIKKEI'],
    HK50: ['HANGSENG', 'HSI'],
    AUS200: ['ASX'],
    NL25: ['AEX'],
    SWI20: ['SMI'],
    ESP35: ['IBEX'],
    SG30: ['STI'],
    IN50: ['NIFTY'],
    BRKB: ['BERKSHIRE'],
    GOOGL: ['GOOGLE'],
    META: ['FACEBOOK'],
};
/** Upper case without spaces, slashes, dots or dashes: "xau/usd" → "XAUUSD", "brk.b" → "BRKB". */
const norm = (s) => s.toUpperCase().replace(/[^A-Z0-9&]/g, '');
/**
 * Instruments matching `query`, best first: exact symbol, then exact alias or code, then symbol prefix, then alias
 * prefix, then a word of the name, then anything containing the query. Ties go to the higher `weight` (24h volume).
 */
export function searchInstruments(list, query, weight = () => 0) {
    const q = norm(query);
    if (!q)
        return [];
    const hits = [];
    for (const item of list) {
        const sym = item.symbol;
        const tag = instrumentTag(sym);
        const label = pairLabel(sym, item.assetClass);
        // The full pair ("BTCUSDT", "EURUSD") counts as the symbol; codes and aliases are reported as what matched.
        const own = [norm(sym), norm(label)];
        const other = [...(tag ? [tag] : []), ...(ALIASES[sym] ?? [])];
        const name = item.name.toUpperCase();
        let score = 0;
        let via = null;
        const alias = (test) => other.find((a) => test(norm(a)));
        if (own.includes(q))
            score = 100;
        else if ((via = alias((a) => a === q) ?? null))
            score = 90;
        else if (own.some((s) => s.startsWith(q)))
            score = 80;
        else if ((via = alias((a) => a.startsWith(q)) ?? null))
            score = 70;
        else if (norm(name).startsWith(q) || name.split(/[\s/()&.-]+/).some((w) => w.startsWith(q)))
            score = 60;
        else if (norm(sym).includes(q) || norm(name).includes(q))
            score = 30;
        if (score)
            hits.push({ item, score, via });
    }
    return hits.sort((a, b) => b.score - a.score || weight(b.item) - weight(a.item) || a.item.symbol.localeCompare(b.item.symbol));
}
//# sourceMappingURL=search.js.map